import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { cn } from '../../../../lib/utils';
import {
  borderPrimary,
  semiBoldText,
  primaryText,
  largeTextSize,
  smallTextSize,
  secondaryText,
  whiteBG,
  baseTextSize,
  mediumText,
} from '../../../../styling/stylingHelper';
import { CostPriceAnalysis } from '../../../../services/costPrice/costPriceService';
import { eur } from '../../../orderhistory/utils';

interface EditMenuPriceModalProps {
  analysis: CostPriceAnalysis;
  currentRevenuePrice?: { menuPrice: number; menuExVatPrice: number } | null;
  onSave: (customPrice: number, customPriceExVat: number) => Promise<void>;
  onReset: () => Promise<void>;
  onClose: () => void;
}

// An incl/ex-VAT pair implies its own rate (incl / ex - 1). Snap it to the two rates this
// toggle offers, but only when it lands close enough: a revenue item aggregated over items
// sold at mixed rates (9% takeaway + 21% dine-in on the same name) sits between the two and
// must NOT be silently rounded into one of them — those return null so the caller falls back.
const VAT_SNAP_TOLERANCE = 0.015;

const impliedVatRate = (inclVat?: number | null, exVat?: number | null): '21' | '9' | null => {
  if (!inclVat || !exVat || exVat <= 0) return null;
  const implied = inclVat / exVat - 1;
  if (Math.abs(implied - 0.09) <= VAT_SNAP_TOLERANCE) return '9';
  if (Math.abs(implied - 0.21) <= VAT_SNAP_TOLERANCE) return '21';
  return null;
};

const EditMenuPriceModal = ({
  analysis,
  currentRevenuePrice,
  onSave,
  onReset,
  onClose
}: EditMenuPriceModalProps) => {
  const { t } = useTranslation();
  // VAT prefill priority: the rate implied by an already-saved custom price (what the user
  // picked last time) → the rate the linked revenue item is actually booked at → the dish
  // type default (FOOD 9%, else 21%). Defaulting to the type alone showed 21% next to a
  // revenue item sold at 9%, and saving then wrote an ex-VAT price that was too low.
  const savedVatRate = impliedVatRate(analysis.customMenuPrice, analysis.customMenuPriceExVat);
  const revenueVatRate = impliedVatRate(currentRevenuePrice?.menuPrice, currentRevenuePrice?.menuExVatPrice);
  const typeVatRate: '21' | '9' = analysis.costPriceType === 'FOOD' ? '9' : '21';
  const initialVatRate: '21' | '9' = savedVatRate ?? revenueVatRate ?? typeVatRate;

  const [customPrice, setCustomPrice] = useState<string>('');
  const [vatRate, setVatRate] = useState<'21' | '9'>(initialVatRate);
  const [vatRateTouched, setVatRateTouched] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const selectVatRate = (rate: '21' | '9') => {
    setVatRate(rate);
    setVatRateTouched(true);
  };

  // Revenue history can still be loading when the modal mounts, so re-apply the derived rate
  // until the user picks one themselves.
  useEffect(() => {
    if (vatRateTouched) return;
    setVatRate(initialVatRate);
  }, [initialVatRate, vatRateTouched]);

  // Initialize with current custom price or empty
  useEffect(() => {
    if (analysis.customMenuPrice) {
      setCustomPrice(analysis.customMenuPrice.toFixed(2));
    }
  }, [analysis.customMenuPrice]);

  const handleSave = async () => {
    const priceValue = parseFloat(customPrice);

    if (isNaN(priceValue) || priceValue <= 0) {
      return;
    }

    setIsSaving(true);
    try {
      // Calculate ex-VAT price based on selected VAT rate
      const vatDivisor = vatRate === '21' ? 1.21 : 1.09;
      const exVatPrice = priceValue / vatDivisor;
      await onSave(priceValue, exVatPrice);
      onClose();
    } catch (error) {
      console.error('Failed to save custom menu price:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleResetToDefault = async () => {
    setIsSaving(true);
    try {
      await onReset();
      onClose();
    } catch (error) {
      console.error('Failed to reset menu price:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const vatDivisor = vatRate === '21' ? 1.21 : 1.09;
  const calculatedExVat = customPrice ? (parseFloat(customPrice) / vatDivisor).toFixed(2) : '0.00';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 dark:bg-black/60" onClick={onClose}>
      <div
        className={cn(
          "bg-white dark:bg-background-dark rounded-xl shadow-xl w-full max-w-md border",
          borderPrimary
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4">
          {/* Header */}
          <div className="flex justify-between items-start mb-4 relative">
            <h2 className={cn(semiBoldText, largeTextSize, primaryText)}>
              {t('costPrice.editMenuPrice.title')}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          <div className="space-y-4 mb-6">
            <p className={cn("text-sm mb-4", secondaryText)}>
              {t('costPrice.editMenuPrice.description')}
            </p>

            {/* Show current revenue price if available */}
            {currentRevenuePrice && (
              <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl">
                <p className="text-xs text-blue-600 dark:text-blue-400 mb-1">
                  {t('costPrice.editMenuPrice.currentRevenuePrice')}
                </p>
                <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                  {eur(currentRevenuePrice.menuPrice)}
                  <span className="text-xs ml-2 opacity-70">
                    (Ex VAT: {eur(currentRevenuePrice.menuExVatPrice)})
                  </span>
                </p>
              </div>
            )}

            {/* VAT Rate Selector */}
            <div>
              <label className={cn(mediumText, baseTextSize, secondaryText, "mb-2 block")}>
                {t('costPrice.vatRate.label')} *
              </label>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => selectVatRate('21')}
                  disabled={isSaving}
                  className={cn(
                    "flex-1 px-4 py-3 rounded-xl border transition-colors",
                    vatRate === '21'
                      ? "border-darkGreenColor dark:border-darkGreenColor-dark bg-[#F1FAF7] dark:bg-cardLightGreen-dark text-darkGreenColor dark:text-darkGreenColor-dark font-semibold"
                      : cn(borderPrimary, "bg-white hover:bg-gray-50 dark:hover:bg-backgroundDarker-dark", secondaryText),
                    "disabled:opacity-50"
                  )}
                >
                  21%
                </button>
                <button
                  type="button"
                  onClick={() => selectVatRate('9')}
                  disabled={isSaving}
                  className={cn(
                    "flex-1 px-4 py-3 rounded-xl border transition-colors",
                    vatRate === '9'
                      ? "border-darkGreenColor dark:border-darkGreenColor-dark bg-[#F1FAF7] dark:bg-cardLightGreen-dark text-darkGreenColor dark:text-darkGreenColor-dark font-semibold"
                      : cn(borderPrimary, "bg-white hover:bg-gray-50 dark:hover:bg-backgroundDarker-dark", secondaryText),
                    "disabled:opacity-50"
                  )}
                >
                  9%
                </button>
              </div>
              <p className={cn("mt-1 text-xs", secondaryText)}>
                {!vatRateTouched && !savedVatRate && revenueVatRate
                  ? t('costPrice.vatRate.fromRevenueItem', { rate: revenueVatRate })
                  : t('costPrice.vatRate.helper')}
              </p>
            </div>

            {/* Custom price input */}
            <div>
              <label htmlFor="customPrice" className={cn(mediumText, baseTextSize, secondaryText, "mb-2 block")}>
                {t('costPrice.editMenuPrice.customPrice')} *
              </label>
              <div className="relative">
                <span className={cn("absolute left-4 top-1/2 -translate-y-1/2", secondaryText)}>
                  €
                </span>
                <input
                  type="number"
                  id="customPrice"
                  step="0.01"
                  min="0"
                  value={customPrice}
                  onChange={(e) => setCustomPrice(e.target.value)}
                  className={cn(
                    "w-full pl-10 pr-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-100",
                    borderPrimary,
                    whiteBG,
                    primaryText
                  )}
                  placeholder="0.00"
                  disabled={isSaving}
                />
              </div>
              <p className={cn("mt-1 text-xs", secondaryText)}>
                {t('costPrice.editMenuPrice.priceIncludesVat', { rate: vatRate })}
              </p>
            </div>

            {/* Calculated ex-VAT price */}
            {customPrice && parseFloat(customPrice) > 0 && (
              <div className={cn("mt-3 p-3 bg-gray-50 dark:bg-gray-800/30 rounded-xl border", borderPrimary)}>
                <p className={cn("text-xs mb-1", secondaryText)}>
                  {t('costPrice.editMenuPrice.calculatedExVat')} ({vatRate}% VAT)
                </p>
                <p className={cn("text-sm font-medium", primaryText)}>
                  €{calculatedExVat}
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="-mx-4 mt-4">
            <div className="border-t border-borderPrimary dark:border-borderPrimary-dark w-full" />
            <div className="flex justify-between items-center pt-4 px-4">
              {/* Reset button */}
              {analysis.customMenuPrice && (
                <button
                  onClick={handleResetToDefault}
                  disabled={isSaving}
                  className={cn(
                    smallTextSize,
                    semiBoldText,
                    "px-4 py-3 rounded-xl border border-orange-200 dark:border-orange-800 text-orange-700 dark:text-orange-300 hover:bg-orange-50 dark:hover:bg-orange-900/20 transition disabled:opacity-50"
                  )}
                >
                  {t('costPrice.editMenuPrice.resetToDefault')}
                </button>
              )}

              {/* Action buttons */}
              <div className="flex gap-3 ml-auto">
                <button
                  onClick={onClose}
                  className={cn(
                    smallTextSize,
                    secondaryText,
                    semiBoldText,
                    "rounded-xl px-4 py-3",
                    whiteBG,
                    "border",
                    borderPrimary,
                    "hover:bg-gray-50 dark:hover:bg-backgroundDarker-dark",
                    "disabled:opacity-50"
                  )}
                  disabled={isSaving}
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handleSave}
                  disabled={isSaving || !customPrice || parseFloat(customPrice) <= 0}
                  className={cn(
                    smallTextSize,
                    "text-white dark:text-white",
                    semiBoldText,
                    "bg-darkGreenColor dark:bg-darkGreenColor-dark rounded-xl px-4 py-3",
                    "hover:bg-darkGreenColor/95",
                    "disabled:opacity-50 disabled:cursor-not-allowed"
                  )}
                >
                  {isSaving ? t('common.saving') : t('common.save')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditMenuPriceModal;
